import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn } from '../../services/authService';
import { sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import '../../styles/admin.css';

export default function LoginPage() {
  const navigate = useNavigate();

  // 'login' | 'reset'
  const [view, setView] = useState('login');

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await signIn(email, password);

      // Fetch user document to determine role-based destination
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const userRoles = userData?.roles ||
        (userData?.role ? [userData.role] : []);

      if (userRoles.includes('admin')) {
        navigate('/admin/funds');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/user-not-found'
      ) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResetRequest(e) {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-email') {
        setResetError('No account found with that email address.');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  }

  function goToReset() {
    setResetEmail(email);
    setResetError('');
    setResetSuccess(false);
    setView('reset');
  }

  function goToLogin() {
    setError('');
    setView('login');
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <h1>Mirepa Investment Advisors</h1>
          <p>Deal Origination & Portfolio Management Portal</p>
        </div>

        {view === 'login' && (
          <>
            <h2 className="auth-title">Sign In</h2>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleLogin}>
              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@mirepa.com"
                  required
                  autoFocus
                />
              </div>
              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your password"
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-full btn-lg"
                disabled={loading}
              >
                {loading ? <span className="spinner spinner-sm" /> : null}
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={goToReset}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-accent)',
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Forgot password?
              </button>
            </div>
            <p style={{ marginTop: 16, fontSize: 12, color: 'var(--color-text-secondary)', textAlign: 'center' }}>
              Accounts are managed by your system administrator.
            </p>
          </>
        )}

        {view === 'reset' && (
          <>
            <h2 className="auth-title">Reset your password</h2>
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
              Enter your email address and we will send you a link to reset your password.
            </p>

            {resetError && <div className="auth-error">{resetError}</div>}
            {resetSuccess && (
              <div className="auth-success">
                Password reset email sent. Please check your inbox and spam folder. The link expires in 1 hour.
              </div>
            )}

            {!resetSuccess && (
              <form onSubmit={handleResetRequest}>
                <div className="form-field" style={{ marginBottom: 24 }}>
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={resetEmail}
                    onChange={e => { setResetEmail(e.target.value); setResetError(''); }}
                    placeholder="you@mirepa.com"
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-full btn-lg"
                  disabled={resetLoading}
                >
                  {resetLoading ? <span className="spinner spinner-sm" /> : null}
                  {resetLoading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            )}

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                type="button"
                onClick={goToLogin}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-accent)',
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                ← Back to login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
